import { SizeOption } from '../../../../types';
import { type BreedWeight } from './BreedDataset';

// picks the size with the highest probability from a BreedWeight distribution.
// kept dependency-free so both the variant selector and the quiz data layer can
// share one argmax without dragging in the sizing guide component's imports.
export function getSizeOption(item: BreedWeight): SizeOption {
  const sizes = {
    [SizeOption.XSmall]: item.XS,
    [SizeOption.Small]: item.S,
    [SizeOption.Medium]: item.M,
    [SizeOption.Large]: item.L,
    [SizeOption.XLarge]: item.XL,
  };

  const largestSize = Object.keys(sizes).reduce((a, b) => (sizes[a as SizeOption] > sizes[b as SizeOption] ? a : b));

  return largestSize as SizeOption;
}

export interface DistributionRow {
  size: SizeOption;
  percentage: number;
}

/**
 * The distribution as display rows: rounded, most-bought first, and without the sizes nobody
 * bought.
 *
 * The result used to print a fixed three cells — top size, runner-up, and "Other" — which turned a
 * clean two-way split into a meaningless "Other 0%" and hid whether the runner-up was 1% or 49%.
 * Listing the real sizes is both shorter and more honest.
 */
export function distributionRows(item: BreedWeight): DistributionRow[] {
  return (
    [
      { size: SizeOption.XSmall, percentage: Math.round(Number(item.XS) || 0) },
      { size: SizeOption.Small, percentage: Math.round(Number(item.S) || 0) },
      { size: SizeOption.Medium, percentage: Math.round(Number(item.M) || 0) },
      { size: SizeOption.Large, percentage: Math.round(Number(item.L) || 0) },
      { size: SizeOption.XLarge, percentage: Math.round(Number(item.XL) || 0) },
    ] satisfies DistributionRow[]
  )
    .filter((row) => row.percentage > 0)
    .sort((a, b) => b.percentage - a.percentage);
}
